import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { authApi } from '@/auth/api';
import { useResendEmailVerification } from '@/features/users';
import { useApiErrorMessage } from '@/shared/data/errors';
import { s } from '@/theme/tokens';
import { Box, FormCard, Inline, Section, Stack, Text } from '@/ui';
import { Alert, Button, Link } from '@/ui/primitives';
import { PageShell } from '@/ui/primitives/PageShell';

type VerifyEmailPageProps = {
  session: { userId: string; orgId?: string; emailVerified?: boolean } | null;
};

type VerificationState = 'verifying' | 'verified' | 'invalid';

const invalidTokenMessage = 'This verification link is invalid or expired.';

export const VerifyEmailPage = ({ session }: VerifyEmailPageProps): ReactElement => {
  const { token } = useParams<{ token: string }>();
  const toErrorMessage = useApiErrorMessage();
  const resendVerification = useResendEmailVerification();
  const [state, setState] = useState<VerificationState>('verifying');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const verificationToken = token?.trim() ?? '';
  const hasToken = verificationToken.length >= 10;
  const showResend = useMemo(() => Boolean(session) && session?.emailVerified !== true, [session]);

  useEffect(() => {
    if (!hasToken) {
      setState('invalid');
      return;
    }
    let mounted = true;
    const verify = async (): Promise<void> => {
      try {
        await authApi.verifyEmail(verificationToken);
        if (!mounted) return;
        setState('verified');
      } catch {
        if (!mounted) return;
        setState('invalid');
      }
    };
    void verify();
    return () => {
      mounted = false;
    };
  }, [hasToken, verificationToken]);

  if (state === 'verified' && session?.emailVerified) {
    return <Navigate to="/account-settings" replace />;
  }

  return (
    <PageShell maxWidth="sm" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="lg" sx={{ pt: { xs: s('xl'), sm: s('5xl') }, gap: { xs: s('lg'), sm: s('xl') } }}>
        <Section>
          <Stack gap="lg" alignItems="center">
            <Box sx={{ width: '100%' }}>
              <FormCard
                title="Verify Email"
                contentGap="sm"
                footer={
                  <Inline gap="sm">
                    <Text role="body" color="textSecondary">
                      {session ? 'Back to account settings?' : 'Back to sign in?'}
                    </Text>
                    <Link
                      component={RouterLink}
                      to={session ? '/account-settings' : '/sign-in'}
                      underline="hover"
                      variant="body2"
                    >
                      Continue
                    </Link>
                  </Inline>
                }
              >
                {state === 'verifying' ? (
                  <Alert severity="info" color="blue">
                    Verifying your email address...
                  </Alert>
                ) : null}
                {state === 'verified' ? (
                  <Alert
                    severity="success"
                    color="green"
                    variant="outlined"
                    sx={{
                      border: '1px solid',
                      borderColor: 'green.main',
                      color: 'green.main',
                      '& .MuiAlert-icon': { color: 'green.main' },
                    }}
                  >
                    Your email has been verified successfully.
                  </Alert>
                ) : null}
                {state === 'invalid' ? (
                  <Alert severity="error" color="red">
                    {invalidTokenMessage}
                  </Alert>
                ) : null}
                {submitError ? (
                  <Alert severity="error" color="red">
                    {submitError}
                  </Alert>
                ) : null}
                {showResend ? (
                  <Inline gap="md" align="center">
                    <Text role="body" color="textSecondary">
                      Need a fresh verification link?
                    </Text>
                    <Button
                      variant="outlined"
                      disableElevation
                      fullWidthOnMobile={false}
                      disabled={resendVerification.isPending}
                      onClick={() => {
                        setSubmitError(null);
                        resendVerification.mutate(undefined, {
                          onSuccess: () => {
                            setSubmitError(null);
                          },
                          onError: (error) => {
                            setSubmitError(toErrorMessage(error));
                          },
                        });
                      }}
                    >
                      {resendVerification.isPending ? 'Sending...' : 'Resend Verification Email'}
                    </Button>
                  </Inline>
                ) : null}
              </FormCard>
            </Box>
          </Stack>
        </Section>
      </Stack>
    </PageShell>
  );
};
